import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./MapWithConsent.module.css";
import CustomButton from "../components/CustomButton";

const CONSENT_KEY = "mapConsent_session"; 


const IFRAME_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2429.4385148592124!2d13.3092581!3d52.4602024!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a85a7dc9bb0b9f%3A0x1f417ad3eca76a32!2sGrunewaldstra%C3%9Fe%2024%2C%2012165%20Berlin!5e0!3m2!1sde!2sde!4v1717682960000!5m2!1sde!2sde";

const OPEN_LINK =
  "https://www.google.com/maps/dir/?api=1&destination=Grunewaldstra%C3%9Fe+24,+12165+Berlin";

const LOAD_TIMEOUT_MS = 9000;

const MapWithConsent: React.FC = () => {
  const [consentGiven, setConsentGiven] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  const [iframeKey, setIframeKey] = useState(0);

  const timeoutRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startLoading = useCallback(() => {
    clearTimer();
    setIsLoading(true);
    setShowFallback(false);

    timeoutRef.current = window.setTimeout(() => {
      setIsLoading(false);
      setShowFallback(true);
    }, LOAD_TIMEOUT_MS);
  }, [clearTimer]);

  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem(CONSENT_KEY);
      if (stored === "true") setConsentGiven(true);
    } catch {

    }
  }, []);

  useEffect(() => {
    if (consentGiven) startLoading();
    return () => clearTimer();
  }, [consentGiven, startLoading, clearTimer]);

  const handleConsent = useCallback(() => {
    try {
      window.sessionStorage.setItem(CONSENT_KEY, "true");
    } catch {
    
    }
    setConsentGiven(true);
  
  }, []);

  const handleRevoke = useCallback(() => {
    try {
      window.sessionStorage.removeItem(CONSENT_KEY);
    } catch {
  
    }
    clearTimer();
    setConsentGiven(false);
    setIsLoading(false);
    setShowFallback(false);
    setIframeKey((v) => v + 1); 
  }, [clearTimer]);

  const handleRetry = useCallback(() => {
    setShowFallback(false);
    setIframeKey((v) => v + 1); 
    startLoading();
  }, [startLoading]);

  const onIframeLoad = useCallback(() => {

    clearTimer();
    setIsLoading(false);
  }, [clearTimer]);

  const title = useMemo(
    () => "Standort auf Google Maps: Grunewaldstraße 24, 12165 Berlin",
    []
  );

  return (
    <div className={styles.wrapper}>
      {!consentGiven ? (
        <div className={styles.placeholder}>
          <div className={styles.placeholderInner}>
            <p className={styles.placeholderText}>
              Um die interaktive Karte anzuzeigen, aktivieren Sie bitte Google Maps.
              <br />
              Beim Aktivieren wird eine Verbindung zu Google hergestellt. Dabei können
              personenbezogene Daten (z.&nbsp;B. IP-Adresse) übertragen werden.
              Weitere Informationen finden Sie in unserer{" "}
              <a className={styles.link} href="/datenschutz">
                Datenschutzerklärung
              </a>
              .
            </p>

            <div className={styles.actions}>
              <CustomButton
                text="Karte aktivieren"
                variant="filled"
                size="large"
                onClick={handleConsent}
              />

              <a
                className={styles.secondaryLink}
                href={OPEN_LINK}
                target="_blank"
                rel="noopener noreferrer"
              >
                In Google Maps öffnen
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.mapBlock}>
          <div className={styles.frameShell} aria-busy={isLoading ? "true" : "false"}>
            <iframe
              key={iframeKey}
              title={title}
              src={IFRAME_SRC}
              className={styles.mapFrame}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              onLoad={onIframeLoad}
            />

            {isLoading && (
              <div className={styles.loadingOverlay} role="status" aria-live="polite">
                <div className={styles.spinner} />
                <div className={styles.loadingText}>Karte wird geladen…</div>
              </div>
            )}

            {showFallback && (
              <div className={styles.fallbackOverlay} role="note" aria-live="polite">
                <div className={styles.fallbackInner}>
                  <div className={styles.fallbackTitle}>Karte konnte nicht geladen werden</div>
                  <div className={styles.fallbackText}>
                    Je nach Browser-/Datenschutzeinstellungen kann Google Maps blockiert sein.
                    Sie können es erneut versuchen oder den Standort direkt in Google Maps öffnen.
                  </div>

                  <div className={styles.fallbackActions}>
                    <CustomButton
                      text="Erneut versuchen"
                      variant="filled"
                      size="large"
                      onClick={handleRetry}
                    />
                    <a
                      className={styles.fallbackLink}
                      href={OPEN_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      In Google Maps öffnen
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.controlsRow}>
            <CustomButton
              text="Karte ausblenden"
              variant="filled"
              size="large"
              onClick={handleRevoke}
            />

            <a
              className={styles.secondaryLinkInline}
              href={OPEN_LINK}
              target="_blank"
              rel="noopener noreferrer"
            >
              In Google Maps öffnen
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapWithConsent;
